import { ApiService } from '@sparklayer-io/spark-dash-core/types/api';
import type { ApiHandlerContract } from '@sparklayer-io/spark-dash-core/types/handlers/api';
import type { CompanyLookup, CompanyRequest } from '../types/api';
import type { CompanyDetail } from '../types/company';
import { CompanyAddressesApi, type CompanyAddressesApiContract } from './company-addresses.api';
import { SectionsApi, type SectionsApiContract } from './sections.api';

/**
 * A service contract to interact with a specific company via the SparkLayer API.
 */
export interface CompanyApiContract {
  get(): Promise<CompanyDetail>;
  update(payload: CompanyRequest): Promise<CompanyDetail>;
  delete(): Promise<void>;
  sections(): SectionsApiContract;
  addresses(): CompanyAddressesApiContract;
}

/**
 * A service to interact with a specific company via the SparkLayer API.
 */
export class CompanyApi extends ApiService implements CompanyApiContract {
  constructor(
    api: ApiHandlerContract,
    private companyLookup: CompanyLookup,
  ) {
    super(api);
  }

  public get(): Promise<CompanyDetail> {
    return this.api.proxy({
      method: 'GET',
      path: '/v2/companies/{lookupBy}/{identifier}',
      params: {
        lookupBy: this.companyLookup.by,
        identifier: this.companyLookup.identifier,
      },
    });
  }

  public update(payload: CompanyRequest): Promise<CompanyDetail> {
    return this.api.proxy({
      method: 'PUT',
      path: '/v2/companies/{lookupBy}/{identifier}',
      payload,
      params: {
        lookupBy: this.companyLookup.by,
        identifier: this.companyLookup.identifier,
      },
    });
  }

  public delete(): Promise<void> {
    return this.api.proxy({
      method: 'DELETE',
      path: '/v2/companies/{lookupBy}/{identifier}',
      params: {
        lookupBy: this.companyLookup.by,
        identifier: this.companyLookup.identifier,
      },
    });
  }

  sections(): SectionsApiContract {
    return new SectionsApi(this.api, this.companyLookup);
  }

  addresses(): CompanyAddressesApiContract {
    return new CompanyAddressesApi(this.api, this.companyLookup);
  }
}
